import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePromptQueue, PromptPriority } from "@/contexts/PromptQueueContext";

export function TermsAcceptanceDialog() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isOwnerLogin = location.pathname === "/owner-login";
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();

  const [authSettled, setAuthSettled] = useState(false);

  // Delay query until auth has settled to avoid post-login stampede.
  // For users who just signed up via onboarding, wait 2s so the inline
  // accepted_terms_at write completes before we query.
  useEffect(() => {
    if (!user?.id) { setAuthSettled(false); return; }
    const justSignedUp = localStorage.getItem("grounded_just_signed_up") === "1";
    const delay = justSignedUp ? 2000 : 1500;
    const t = setTimeout(() => {
      setAuthSettled(true);
      if (justSignedUp) localStorage.removeItem("grounded_just_signed_up");
    }, delay);
    return () => clearTimeout(t);
  }, [user?.id]);

  const { data: needsAcceptance } = useQuery({
    queryKey: ["terms-acceptance", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .from("profiles")
        .select("accepted_terms_at")
        .eq("id", user.id)
        .single();
      if (error) return false;
      return !(data as any)?.accepted_terms_at;
    },
    enabled: !!user?.id && authSettled && !isOwnerLogin,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (needsAcceptance && !isOwnerLogin) {
      requestPrompt('terms', PromptPriority.TERMS);
    }
  }, [needsAcceptance, isOwnerLogin]);

  const handleAccept = async () => {
    if (!user?.id || !accepted) return;
    setSubmitting(true);
    try {
      await supabase
        .from("profiles")
        .update({ accepted_terms_at: new Date().toISOString() } as any)
        .eq("id", user.id);
      queryClient.setQueryData(["terms-acceptance", user.id], false);
      releasePrompt('terms');
    } finally {
      setSubmitting(false);
    }
  };

  if (isOwnerLogin || !needsAcceptance || !isActivePrompt('terms')) return null;

  return (
    <Dialog open={true}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Terms of Service</DialogTitle>
          <DialogDescription>
            Please review and accept our Terms of Service to continue using Grounded.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="text-sm text-muted-foreground space-y-2">
            <p>By using Grounded, you agree to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Not post objectionable, abusive, or inappropriate content</li>
              <li>Respect other community members</li>
              <li>Accept that violations may result in account removal</li>
            </ul>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="terms-accept"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
            />
            <label htmlFor="terms-accept" className="text-sm leading-tight cursor-pointer">
              I agree to the{" "}
              <Link to="/terms" className="text-primary underline" target="_blank">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-primary underline" target="_blank">
                Privacy Policy
              </Link>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleAccept} disabled={!accepted || submitting} className="w-full">
            {submitting ? "Accepting..." : "Accept & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
